import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/services/api";
import { useApiReady } from "@/hooks/useApi";

interface CanonicalMetadata {
  system?: string;
  model?: string;
  input_messages?: { role: string; content: string }[];
  output_messages?: { role: string; content: string }[];
  input_text?: string;
  output_text?: string;
  input_tokens?: number | null;
  output_tokens?: number | null;
  total_tokens?: number | null;
  tools_called?: string[];
  has_tool_calls?: boolean;
  latency_ms?: number | null;
}

interface TraceDetail {
  id: number;
  source: string;
  source_id: string;
  event_type: string;
  severity: string;
  timestamp: string;
  correlation_id: string | null;
  anomaly_score: number;
  event_metadata: Record<string, unknown> | null;
  canonical_metadata: CanonicalMetadata | null;
  agent_id: number | null;
  agent_name: string | null;
}

interface TraceInspectorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  traceId: number | null;
  agentName?: string;
}

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: "bg-red-100 text-red-800 border-red-200",
  HIGH: "bg-orange-100 text-orange-800 border-orange-200",
  MEDIUM: "bg-yellow-100 text-yellow-800 border-yellow-200",
  LOW: "bg-blue-100 text-blue-800 border-blue-200",
  INFO: "bg-gray-100 text-gray-800 border-gray-200",
};

export function TraceInspectorSheet({
  open,
  onOpenChange,
  traceId,
  agentName,
}: TraceInspectorSheetProps) {
  const isApiReady = useApiReady();
  const { data: trace, isLoading } = useQuery<TraceDetail>({
    queryKey: ["trace-detail", traceId],
    queryFn: () => apiClient.get(`/api/v1/traces/${traceId}`),
    enabled: !!traceId && open && isApiReady,
    staleTime: 60000,
  });

  const em = trace?.event_metadata;
  const displayName = agentName || trace?.agent_name || "Unknown Agent";

  // Build effective canonical metadata: use canonical_metadata if populated,
  // otherwise synthesize from event_metadata fields
  const cm = (() => {
    const raw = trace?.canonical_metadata;
    if (raw && (
      (raw.input_messages && raw.input_messages.length > 0) ||
      (raw.output_messages && raw.output_messages.length > 0) ||
      raw.input_text || raw.output_text
    )) {
      return raw;
    }
    if (!em) return raw;

    const inputMsgs: { role: string; content: string }[] = [];
    const outputMsgs: { role: string; content: string }[] = [];
    for (const m of (em.gen_ai_input_messages as { role: string; content: string }[] ?? [])) {
      if (m && typeof m === "object" && m.content) inputMsgs.push(m);
    }
    for (const m of (em.gen_ai_output_messages as { role: string; content: string }[] ?? [])) {
      if (m && typeof m === "object" && m.content) outputMsgs.push(m);
    }
    if (inputMsgs.length === 0 && outputMsgs.length === 0) return raw;

    return {
      ...raw,
      system: raw?.system ?? (em.gen_ai_system as string) ?? undefined,
      model: raw?.model ?? (em.gen_ai_request_model as string) ?? undefined,
      input_messages: inputMsgs,
      output_messages: outputMsgs,
      input_tokens: raw?.input_tokens ?? (em.gen_ai_input_tokens as number) ?? null,
      output_tokens: raw?.output_tokens ?? (em.gen_ai_output_tokens as number) ?? null,
      total_tokens: raw?.total_tokens ?? (
        em.gen_ai_input_tokens != null && em.gen_ai_output_tokens != null
          ? (em.gen_ai_input_tokens as number) + (em.gen_ai_output_tokens as number)
          : null
      ),
      latency_ms: raw?.latency_ms ?? (em.duration_ms as number) ?? null,
    } satisfies CanonicalMetadata;
  })();

  const hasCanonicalContent =
    (cm?.input_messages && cm.input_messages.length > 0) ||
    (cm?.output_messages && cm.output_messages.length > 0) ||
    !!cm?.input_text ||
    !!cm?.output_text;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[38.2vw] sm:max-w-[38.2vw] min-w-[400px] overflow-y-auto">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <span>{displayName}</span>
            {traceId && (
              <span className="text-sm font-mono text-muted-foreground">
                #{traceId}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : trace ? (
          <div className="space-y-6 pb-6">
            {/* Metadata */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Source</span>
                <p className="font-medium">{trace.source}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Event Type</span>
                <p className="font-medium">{trace.event_type}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Severity</span>
                <div className="mt-0.5">
                  <Badge
                    variant="outline"
                    className={SEVERITY_COLORS[trace.severity] || ""}
                  >
                    {trace.severity}
                  </Badge>
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Timestamp</span>
                <p className="font-medium">
                  {new Date(trace.timestamp).toLocaleString()}
                </p>
              </div>
              {cm?.model && (
                <div>
                  <span className="text-muted-foreground">Model</span>
                  <p className="font-medium">{cm.model}</p>
                </div>
              )}
              {cm?.system && (
                <div>
                  <span className="text-muted-foreground">Provider</span>
                  <p className="font-medium">{cm.system}</p>
                </div>
              )}
            </div>

            {/* Token Usage */}
            {cm && (cm.input_tokens != null || cm.output_tokens != null) && (
              <div>
                <h4 className="text-sm font-medium mb-2">Token Usage</h4>
                <div className="flex gap-4 text-sm">
                  {cm.input_tokens != null && (
                    <div className="rounded-lg bg-muted/50 px-3 py-2">
                      <span className="text-muted-foreground">Input: </span>
                      <span className="font-mono font-medium">
                        {cm.input_tokens.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {cm.output_tokens != null && (
                    <div className="rounded-lg bg-muted/50 px-3 py-2">
                      <span className="text-muted-foreground">Output: </span>
                      <span className="font-mono font-medium">
                        {cm.output_tokens.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {cm.total_tokens != null && (
                    <div className="rounded-lg bg-muted/50 px-3 py-2">
                      <span className="text-muted-foreground">Total: </span>
                      <span className="font-mono font-medium">
                        {cm.total_tokens.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Latency */}
            {cm?.latency_ms != null && (
              <div className="text-sm">
                <span className="text-muted-foreground">Latency: </span>
                <span className="font-mono font-medium">
                  {cm.latency_ms.toFixed(0)}ms
                </span>
              </div>
            )}

            {/* Tool Calls */}
            {cm?.has_tool_calls && cm.tools_called && cm.tools_called.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Tool Calls</h4>
                <div className="flex flex-wrap gap-1">
                  {cm.tools_called.map((tool, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {tool}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Input Messages */}
            {cm?.input_messages && cm.input_messages.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Input</h4>
                <div className="space-y-2">
                  {cm.input_messages.map((msg, i) => (
                    <div key={i} className="rounded-lg border p-3">
                      <Badge variant="outline" className="text-xs mb-1">
                        {msg.role}
                      </Badge>
                      <p className="text-sm whitespace-pre-wrap mt-1">
                        {msg.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Output Messages */}
            {cm?.output_messages && cm.output_messages.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Output</h4>
                <div className="space-y-2">
                  {cm.output_messages.map((msg, i) => (
                    <div key={i} className="rounded-lg border bg-muted/30 p-3">
                      <Badge variant="outline" className="text-xs mb-1">
                        {msg.role}
                      </Badge>
                      <p className="text-sm whitespace-pre-wrap mt-1">
                        {msg.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fallback: flat text if no structured messages */}
            {(!cm?.input_messages || cm.input_messages.length === 0) &&
              cm?.input_text && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Input Text</h4>
                  <div className="rounded-lg border p-3">
                    <p className="text-sm whitespace-pre-wrap">
                      {cm.input_text}
                    </p>
                  </div>
                </div>
              )}
            {(!cm?.output_messages || cm.output_messages.length === 0) &&
              cm?.output_text && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Output Text</h4>
                  <div className="rounded-lg border bg-muted/30 p-3">
                    <p className="text-sm whitespace-pre-wrap">
                      {cm.output_text}
                    </p>
                  </div>
                </div>
              )}

            {/* Event metadata fallback when canonical has no message content */}
            {!hasCanonicalContent && em && Object.keys(em).length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Trace Metadata</h4>
                <div className="rounded-lg border p-3 space-y-1.5">
                  {Object.entries(em).map(([key, value]) => (
                    <div key={key} className="flex justify-between gap-4 text-sm">
                      <span className="text-muted-foreground font-mono text-xs shrink-0">
                        {key.replace(/_/g, " ")}
                      </span>
                      <span className="font-medium text-right break-all">
                        {typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Anomaly score */}
            {trace.anomaly_score > 0 && (
              <div className="text-sm">
                <span className="text-muted-foreground">Anomaly Score: </span>
                <span className="font-mono font-medium">
                  {(trace.anomaly_score * 100).toFixed(1)}%
                </span>
              </div>
            )}

            {/* Correlation ID */}
            {trace.correlation_id && (
              <div className="text-sm">
                <span className="text-muted-foreground">Correlation ID: </span>
                <span className="font-mono text-xs break-all">
                  {trace.correlation_id}
                </span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Trace not found.
          </p>
        )}

        {trace?.agent_id && (
          <SheetFooter className="mt-4">
            <Button variant="outline" asChild>
              <Link
                to={`/agents/${trace.agent_id}`}
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                View in Agent Page
              </Link>
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
